import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  deleteColumn,
  deleteRow,
  deleteSheet,
  patchUpdateRow,
  patchRenameColumn,
  patchRenameSheet,
  postAddColumn,
  postAddRow,
  postCreateSheet,
} from './sheetsApi';
import { createSheetCache } from './sheetCache';
import { normalizeCellValue, normalizeRowData } from './dataNormalization';

function sameValues(left, right) {
  return left.length === right.length && left.every((row, index) => (
    row.length === right[index].length
    && row.every((value, column) => value === right[index][column])
  ));
}

function chooseSheet(sheetList, preferred, current) {
  return sheetList.find((sheet) => sheet.title === current)?.title
    || sheetList.find((sheet) => String(sheet.title).trim().toLowerCase() === preferred.toLowerCase())?.title
    || sheetList[0]?.title
    || '';
}

export function useSheetsDashboard(accessToken, options = {}) {
  const preferredInitialSheetName = String(options.preferredInitialSheetName || '').trim();
  const cache = useMemo(() => createSheetCache(accessToken), [accessToken]);
  const activeCacheRef = useRef(cache);
  const previousCacheRef = useRef(cache);
  activeCacheRef.current = cache;
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheetState] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [createSheetName, setCreateSheetName] = useState('');
  const [newSheetName, setNewSheetName] = useState('');
  const [selectedColumnName, setSelectedColumnName] = useState('');
  const [newColumnName, setNewColumnName] = useState('');
  const [addColumnName, setAddColumnName] = useState('');
  const [rowDraft, setRowDraftState] = useState([]);
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const selectedSheetRef = useRef('');
  const viewRef = useRef({ sheetName: '', headers: [], rows: [] });
  const draftRef = useRef([]);
  const editingRowRef = useRef(null);
  const pendingDataRef = useRef(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(false);

  const setSelectedSheet = useCallback((name) => {
    selectedSheetRef.current = name;
    setSelectedSheetState(name);
  }, []);

  const setRowDraft = useCallback((nextDraft) => {
    const next = typeof nextDraft === 'function' ? nextDraft(draftRef.current) : nextDraft;
    draftRef.current = next;
    setRowDraftState(next);
  }, []);

  const resetDraft = useCallback(() => {
    pendingDataRef.current = null;
    editingRowRef.current = null;
    setEditingRowIndex(null);
    setRowDraft([]);
  }, [setRowDraft]);

  const applySheetData = useCallback((sheetName, data, reset = false) => {
    if (selectedSheetRef.current !== sheetName) return;
    if (!reset && (editingRowRef.current !== null || draftRef.current.length > 0)) {
      pendingDataRef.current = { sheetName, data };
      return;
    }
    if (reset) resetDraft();
    const nextHeaders = data.headers || [];
    const nextRows = data.rows || [];
    const previous = viewRef.current;
    if (!sameValues([previous.headers], [nextHeaders])) setHeaders(nextHeaders);
    if (!sameValues(previous.rows, nextRows)) setRows(nextRows);
    viewRef.current = { sheetName, headers: nextHeaders, rows: nextRows };
    setSelectedColumnName((current) => nextHeaders.includes(current) ? current : (nextHeaders[0] || ''));
  }, [resetDraft]);

  const handleLoadError = useCallback((requestError) => {
    if (requestError.name === 'AbortError') return;
    if (requestError.status === 401 || requestError.status === 403) {
      cache.invalidateAll();
      setHeaders([]);
      setRows([]);
      viewRef.current = { sheetName: '', headers: [], rows: [] };
      resetDraft();
    }
    setError(requestError.message);
  }, [cache, resetDraft]);

  const canLoadSheets = Boolean(accessToken);
  const canCreateSheet = createSheetName.trim();
  const canDeleteSheet = selectedSheet.trim();
  const canRenameSheet = selectedSheet.trim() && newSheetName.trim();
  const canRenameColumn =
    selectedSheet.trim() && selectedColumnName.trim() && newColumnName.trim();
  const canAddColumn = selectedSheet.trim() && addColumnName.trim();
  const canDeleteColumn = selectedSheet.trim() && selectedColumnName.trim();

  const rowInputs = useMemo(() => {
    const count = Math.max(headers.length > 0 ? headers.length : 3, rowDraft.length);
    return Array.from({ length: count }, (_, index) => rowDraft[index] ?? '');
  }, [headers, rowDraft]);

  const loadSheetData = useCallback(async (sheetName, { force = false } = {}) => {
    if (!sheetName || !accessToken || !mountedRef.current || activeCacheRef.current !== cache) {
      return;
    }

    const requestId = ++requestIdRef.current;
    setSelectedSheet(sheetName);
    setError('');
    if (force) cache.invalidateAll();
    const cachedData = force ? null : cache.getSheet(sheetName);
    const switching = viewRef.current.sheetName !== sheetName;
    if (switching) resetDraft();
    if (cachedData) {
      applySheetData(sheetName, cachedData, switching);
    } else if (switching) {
      setHeaders([]);
      setRows([]);
      viewRef.current = { sheetName, headers: [], rows: [] };
    }
    setLoading(!cachedData);
    setRefreshing(Boolean(cachedData));
    try {
      const data = await cache.fetchSheet(sheetName, { force, revalidate: true });
      if (mountedRef.current && activeCacheRef.current === cache && requestId === requestIdRef.current) {
        applySheetData(sheetName, data, force);
      }
      if (force && activeCacheRef.current === cache) {
        // Refills related sheets after a write without delaying the saved table.
        cache.fetchSnapshot().catch(() => {});
      }
      return data;
    } catch (requestError) {
      if (mountedRef.current && activeCacheRef.current === cache && requestId === requestIdRef.current) handleLoadError(requestError);
    } finally {
      if (mountedRef.current && activeCacheRef.current === cache && requestId === requestIdRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [accessToken, applySheetData, cache, handleLoadError, resetDraft, setSelectedSheet]);

  const loadSheets = useCallback(async ({ force = false, preferredSheetName = '' } = {}) => {
    if (!mountedRef.current || activeCacheRef.current !== cache) return;
    if (!accessToken) {
      setSheets([]);
      setSelectedSheet('');
      setHeaders([]);
      setRows([]);
      viewRef.current = { sheetName: '', headers: [], rows: [] };
      resetDraft();
      return;
    }

    if (force) cache.invalidateAll();
    const snapshotPromise = cache.fetchSnapshot({ force });
    const cachedList = cache.getSheets()?.sheets || [];
    const initialSheet = chooseSheet(cachedList, preferredSheetName || preferredInitialSheetName,
      preferredSheetName || selectedSheetRef.current);
    const cachedData = initialSheet && cache.getSheet(initialSheet);
    if (cachedList.length > 0) {
      setSheets((previous) => JSON.stringify(previous) === JSON.stringify(cachedList) ? previous : cachedList);
      setSelectedSheet(initialSheet);
      if (cachedData) applySheetData(initialSheet, cachedData, viewRef.current.sheetName !== initialSheet);
    }
    const requestId = ++requestIdRef.current;
    setError('');
    setLoading(!cachedData);
    setRefreshing(Boolean(cachedData));
    try {
      const data = await snapshotPromise;
      if (!mountedRef.current || activeCacheRef.current !== cache) return;
      const sheetList = data.sheets || [];
      setSheets((previous) => JSON.stringify(previous) === JSON.stringify(sheetList) ? previous : sheetList);

      if (sheetList.length > 0) {
        // A later sheet selection owns its own response; startup must not switch it back.
        if (requestId !== requestIdRef.current && sheetList.some((sheet) => sheet.title === selectedSheetRef.current)) return;
        const nextSheet = chooseSheet(sheetList, preferredSheetName || preferredInitialSheetName,
          preferredSheetName || selectedSheetRef.current);
        setSelectedSheet(nextSheet);
        const nextData = cache.getSheet(nextSheet);
        if (nextData) applySheetData(nextSheet, nextData, force || viewRef.current.sheetName !== nextSheet);
      } else {
        setSelectedSheet('');
        setHeaders([]);
        setRows([]);
        resetDraft();
        viewRef.current = { sheetName: '', headers: [], rows: [] };
      }
    } catch (requestError) {
      if (mountedRef.current && activeCacheRef.current === cache && requestId === requestIdRef.current) handleLoadError(requestError);
    } finally {
      if (mountedRef.current && activeCacheRef.current === cache && requestId === requestIdRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [accessToken, applySheetData, cache, handleLoadError, preferredInitialSheetName, resetDraft, setSelectedSheet]);

  useEffect(() => {
    mountedRef.current = true;
    if (previousCacheRef.current !== cache) {
      previousCacheRef.current = cache;
      setSheets([]);
      setSelectedSheet('');
      setHeaders([]);
      setRows([]);
      viewRef.current = { sheetName: '', headers: [], rows: [] };
      resetDraft();
    }
    loadSheets();
    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
    };
  }, [cache, loadSheets, resetDraft, setSelectedSheet]);

  useEffect(() => {
    if (editingRowIndex === null && rowDraft.length === 0 && pendingDataRef.current) {
      const pending = pendingDataRef.current;
      pendingDataRef.current = null;
      applySheetData(pending.sheetName, pending.data);
    }
  }, [applySheetData, editingRowIndex, rowDraft]);

  async function handleRenameSheet(event) {
    event.preventDefault();
    if (!canRenameSheet) {
      return false;
    }

    setError('');
    setLoading(true);
    try {
      await patchRenameSheet(selectedSheet, newSheetName, accessToken);
      if (!mountedRef.current || activeCacheRef.current !== cache) return false;
      setNewSheetName('');
      await loadSheets({ force: true, preferredSheetName: newSheetName });
      return true;
    } catch (requestError) {
      if (!mountedRef.current || activeCacheRef.current !== cache) return false;
      setError(requestError.message);
      return false;
    } finally {
      if (mountedRef.current && activeCacheRef.current === cache) setLoading(false);
    }
  }

  async function handleCreateSheet(event) {
    event.preventDefault();
    if (!canCreateSheet) {
      return false;
    }

    setError('');
    setLoading(true);
    try {
      await postCreateSheet(createSheetName, accessToken);
      if (!mountedRef.current || activeCacheRef.current !== cache) return false;
      setCreateSheetName('');
      await loadSheets({ force: true, preferredSheetName: createSheetName });
      return true;
    } catch (requestError) {
      if (!mountedRef.current || activeCacheRef.current !== cache) return false;
      setError(requestError.message);
      return false;
    } finally {
      if (mountedRef.current && activeCacheRef.current === cache) setLoading(false);
    }
  }

  async function handleDeleteSheet() {
    if (!selectedSheet) {
      return;
    }

    setError('');
    setLoading(true);
    try {
      await deleteSheet(selectedSheet, accessToken);
      if (!mountedRef.current || activeCacheRef.current !== cache) return false;
      await loadSheets({ force: true });
    } catch (requestError) {
      if (!mountedRef.current || activeCacheRef.current !== cache) return false;
      setError(requestError.message);
    } finally {
      if (mountedRef.current && activeCacheRef.current === cache) setLoading(false);
    }
  }

  async function handleRenameColumn(event) {
    event.preventDefault();
    if (!canRenameColumn) {
      return false;
    }

    setError('');
    setLoading(true);
    try {
      await patchRenameColumn(
        selectedSheet,
        selectedColumnName,
        newColumnName,
        accessToken
      );
      if (!mountedRef.current || activeCacheRef.current !== cache) return false;
      setNewColumnName('');
      await loadSheetData(selectedSheet, { force: true });
      return true;
    } catch (requestError) {
      if (!mountedRef.current || activeCacheRef.current !== cache) return false;
      setError(requestError.message);
      return false;
    } finally {
      if (mountedRef.current && activeCacheRef.current === cache) setLoading(false);
    }
  }

  async function handleAddColumn(event) {
    event.preventDefault();
    if (!canAddColumn) {
      return false;
    }

    setError('');
    setLoading(true);
    try {
      await postAddColumn(selectedSheet, addColumnName, accessToken);
      if (!mountedRef.current || activeCacheRef.current !== cache) return false;
      setAddColumnName('');
      await loadSheetData(selectedSheet, { force: true });
      return true;
    } catch (requestError) {
      if (!mountedRef.current || activeCacheRef.current !== cache) return false;
      setError(requestError.message);
      return false;
    } finally {
      if (mountedRef.current && activeCacheRef.current === cache) setLoading(false);
    }
  }

  async function handleDeleteColumn() {
    if (!canDeleteColumn) {
      return;
    }

    setError('');
    setLoading(true);
    try {
      await deleteColumn(selectedSheet, selectedColumnName, accessToken);
      if (!mountedRef.current || activeCacheRef.current !== cache) return false;
      await loadSheetData(selectedSheet, { force: true });
    } catch (requestError) {
      if (!mountedRef.current || activeCacheRef.current !== cache) return false;
      setError(requestError.message);
    } finally {
      if (mountedRef.current && activeCacheRef.current === cache) setLoading(false);
    }
  }

  async function submitRow(rowDataOverride = rowInputs, options = {}) {
    if (!selectedSheet) {
      return false;
    }

    setError('');
    setLoading(true);
    try {
      const savedSheetName = selectedSheet;
      const savedEditIndex = editingRowIndex;
      const savedHeaders = [...headers];
      const normalizedRow = normalizeRowData(selectedSheet, savedHeaders, rowDataOverride);
      const emailIndex = savedHeaders.findIndex((header) => String(header || '').trim().toLowerCase() === 'email');
      const savedEmail = emailIndex >= 0 ? String(normalizedRow[emailIndex] || '').trim().toLowerCase() : '';
      let saveResult;
      if (editingRowIndex === null) {
        saveResult = await postAddRow(selectedSheet, normalizedRow, accessToken);
      } else {
        saveResult = await patchUpdateRow(
          selectedSheet,
          editingRowIndex,
          normalizedRow,
          accessToken,
          options.duplicateResolution || ''
        );
      }
      if (!mountedRef.current || activeCacheRef.current !== cache) return false;
      resetDraft();
      const reloadedData = await loadSheetData(selectedSheet, { force: true });
      if (!mountedRef.current || activeCacheRef.current !== cache || selectedSheetRef.current !== savedSheetName) return false;
      const savedView = viewRef.current;
      const currentEmailIndex = savedView.headers.findIndex((header) => String(header || '').trim().toLowerCase() === 'email');
      // Duplicate resolution can delete rows and move the retained user's index.
      const matchingEmailIndex = savedEmail && currentEmailIndex >= 0
        ? savedView.rows.findIndex((row) => String(row[currentEmailIndex] || '').trim().toLowerCase() === savedEmail)
        : -1;
      const nextEditIndex = matchingEmailIndex >= 0 ? matchingEmailIndex
        : (Number.isInteger(saveResult?.rowIndex) ? saveResult.rowIndex : savedEditIndex);
      const requiresEmailMatch = savedSheetName.trim().toLowerCase() === 'usuarios' && Boolean(savedEmail);
      const savedRow = reloadedData && nextEditIndex !== null
        && (!(requiresEmailMatch || saveResult?.duplicateResolved) || matchingEmailIndex >= 0)
        ? savedView.rows[nextEditIndex] : null;
      if (options.keepEditing && savedRow) {
        editingRowRef.current = nextEditIndex;
        setEditingRowIndex(nextEditIndex);
        setRowDraft(normalizeRowData(savedSheetName, savedView.headers, savedView.headers.map((_, index) => savedRow[index] || '')));
      }
      if (typeof options.onSaved === 'function') {
        options.onSaved({ headers: savedView.headers, row: savedRow, rowIndex: nextEditIndex, result: saveResult });
      }
      return true;
    } catch (requestError) {
      if (!mountedRef.current || activeCacheRef.current !== cache) return false;
      let errorHandled = false;
      if (typeof options.onError === 'function') {
        errorHandled = options.onError(requestError) === true;
      }
      if (!errorHandled) {
        setError(requestError.message);
      }
      return false;
    } finally {
      if (mountedRef.current && activeCacheRef.current === cache) setLoading(false);
    }
  }

  async function handleSubmitRow(event) {
    event.preventDefault();
    await submitRow();
  }

  const handleDeleteRow = useCallback(async (rowIndex, targetEmail = '', deletionNote = '', roleReplacementName = '') => {
    setError('');
    setLoading(true);
    try {
      await deleteRow(
        selectedSheet,
        rowIndex,
        accessToken,
        targetEmail,
        deletionNote,
        roleReplacementName
      );
      if (!mountedRef.current || activeCacheRef.current !== cache) return false;
      resetDraft();
      await loadSheetData(selectedSheet, { force: true });
      return true;
    } catch (requestError) {
      if (!mountedRef.current || activeCacheRef.current !== cache) return false;
      setError(requestError.message);
      return false;
    } finally {
      if (mountedRef.current && activeCacheRef.current === cache) setLoading(false);
    }
  }, [accessToken, cache, loadSheetData, resetDraft, selectedSheet]);

  const startEditRow = useCallback((rowIndex) => {
    editingRowRef.current = rowIndex;
    setEditingRowIndex(rowIndex);
    const currentRow = rows[rowIndex] || [];
    const nextDraft = normalizeRowData(selectedSheet, headers, headers.map((_, index) => currentRow[index] || ''));
    setRowDraft(nextDraft);
  }, [rows, headers, selectedSheet, setRowDraft]);

  const cancelEditRow = useCallback(() => {
    editingRowRef.current = null;
    setEditingRowIndex(null);
    setRowDraft([]);
  }, [setRowDraft]);

  const updateRowDraft = useCallback((index, value, options = {}) => {
    setRowDraft((previous) => {
      const next = [...previous];
      next[index] = value;
      next[index] = normalizeCellValue(headers[index], value, {
        sheetName: selectedSheet,
        headers,
        row: next,
        input: options.input !== false,
      });
      return next;
    });
  }, [headers, selectedSheet, setRowDraft]);

  return {
    sheets,
    selectedSheet,
    headers,
    rows,
    createSheetName,
    newSheetName,
    selectedColumnName,
    newColumnName,
    addColumnName,
    editingRowIndex,
    loading,
    refreshing,
    hasUnsavedDraft: editingRowIndex !== null || rowDraft.length > 0,
    error,
    canLoadSheets,
    canCreateSheet,
    canDeleteSheet,
    canRenameSheet,
    canRenameColumn,
    canAddColumn,
    canDeleteColumn,
    rowInputs,
    setRowDraft,
    setSelectedSheet,
    setCreateSheetName,
    setNewSheetName,
    setSelectedColumnName,
    setNewColumnName,
    setAddColumnName,
    loadSheets,
    loadSheetData,
    handleCreateSheet,
    handleDeleteSheet,
    handleRenameSheet,
    handleRenameColumn,
    handleAddColumn,
    handleDeleteColumn,
    submitRow,
    handleSubmitRow,
    handleDeleteRow,
    startEditRow,
    cancelEditRow,
    updateRowDraft,
  };
}
